import './analytics.css'
import React, { Fragment, useEffect, useState } from 'react'
import { Grid, Box, Card, Stack, Typography, CardContent } from '@mui/material'
import { alpha, styled } from '@mui/system'

import { useRef } from 'react'

import enLocale from 'date-fns/locale/en-US'
import {
    useAnalyzerStore,
    useGetStatsOfMeasure,
    useTranslateMeasurements,
} from 'app/hooks/useAnalyzer'
import Chart from 'react-apexcharts'
import { useTTLBgColor } from 'app/hooks/useTtlColor'
import { useReactToPrint } from 'react-to-print'
import CriteriaRadialChart from './shared/CriteriaRadialChart'
import DataTable from './DataTable'
import AnalyzerToolbar from './AnalyzerToolbar'
import { useDashboardConfig } from 'app/contexts/DashboardConfigContext'
import moment from 'moment'

enLocale.options.weekStartsOn = 1

const ContentBox = styled('div')(({ theme }) => ({
    margin: '30px',
    gap: '2px',
    [theme.breakpoints.down('sm')]: {
        margin: '16px',
    },
}))

export const ChartY = ({ defaultSerial, categories }) => {
    const [state, setState] = React.useState({
        series: defaultSerial || [],
        options: {
            chart: {
                height: 350,
                type: 'line',
                zoom: {
                    enabled: false,
                },
                animations: {
                    enabled: false,
                },
            },
            labels: categories || [],
            title: {
                text: 'Missing data (null values)',
            },
            xaxis: {},
        },
    })

    useEffect(() => {
        // TODO: legend color
        setState({
            series: defaultSerial || [],
            options: {
                chart: {
                    height: 350,
                    type: 'line',
                    toolbar: {
                        show: false,
                    },
                },
                stroke: {
                    width: 3,
                    curve: 'smooth',
                },
                labels: categories || [],

                title: {
                    text: 'แนวโน้มการคัดกรอง',
                    align: 'left',
                    style: {
                        fontSize: '16px',
                        color: '#666',
                    },
                },
                fill: {},
                yaxis: {
                    min: -1,
                },
                xaxis: {
                    type: 'date',
                    tickAmount: 10,
                },
            },
        })
    }, [defaultSerial, categories])

    return (
        <>
            <Card
                elevation={3}
                component={'div'}
                id="overview_chart-1-1368-397"
            >
                <Box p={2}>
                    <Chart
                        height={state.options?.chart?.height || 80}
                        options={state.options}
                        series={state.series}
                        type="line"
                    />
                </Box>
            </Card>
        </>
    )
}

const ChartX = ({ id, title, name, percent, ...props }) => {
    const [series, setSeries] = useState([])
    const [options] = useState({
        chart: {
            height: 160,
            type: 'radialBar',
        },
        stroke: {
            lineCap: 'round',
        },
        plotOptions: {
            radialBar: {
                offsetX: 70,
                hollow: {
                    size: '70%',
                },
                track: {
                    background: 'rgba(0, 104, 56, 0.20)',
                },
                startAngle: -0,
                endAngle: 360,
                dataLabels: {
                    name: {
                        show: false,
                    },
                    value: {
                        offsetY: 10,
                        fontSize: '22px',
                        color: '#fff',
                    },
                },
            },
        },
        fill: {
            colors: ['#FFF'],
        },
        labels: [name || ''],
    })

    const { bg, svg } = useTTLBgColor(name)

    useEffect(() => {
        setSeries([parseFloat(percent.toFixed(2))])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [percent])

    const { format } = useTranslateMeasurements()
    return (
        <>
            <Card
                id={id}
                {...props}
                elevation={3}
                component={'div'}
                sx={{
                    position: 'relative',
                    backgroundColor: bg || '#369FFF',
                    color: 'white',
                }}
            >
                {
                    <>
                        <Stack
                            zIndex={99}
                            position={'absolute'}
                            top={20}
                            left={20}
                            width={'100%'}
                            height={'100%'}
                            spacing={1}
                        >
                            <Typography
                                variant="h6"
                                color={'white'}
                                component="div"
                            >
                                {format(name)}
                            </Typography>
                            <Typography
                                variant="h3"
                                color={'white'}
                                component="div"
                            >
                                {title}
                            </Typography>
                            <Typography color={'white'} component="div">
                                ผลการทดสอบ
                            </Typography>
                        </Stack>
                        <Box
                            position={'absolute'}
                            top={0}
                            left={0}
                            display={'flex'}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="81"
                                height="41"
                                viewBox="0 0 81 41"
                                fill="none"
                            >
                                <path
                                    opacity="0.1"
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M80.619 0C79.5985 6.9096 73.3451 26.3399 47.5834 35.1535C22.5888 43.7047 6.12302 40.7638 6.41017e-05 38L7.02785e-05 20C6.93871e-05 8.95431 8.95439 6.78721e-06 20.0001 5.7411e-06L80.619 0Z"
                                    fill={svg || '#006838'}
                                />
                            </svg>
                        </Box>
                        <Box
                            position={'absolute'}
                            bottom={0}
                            right={0}
                            display={'flex'}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="136"
                                height="80"
                                viewBox="0 0 136 80"
                                fill="none"
                            >
                                <path
                                    opacity="0.1"
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M0.307617 80H116C127.037 80 135.986 71.0592 136 60.025C136 60.0167 136 60.0083 136 60L136 0C135.231 11.6667 121.969 35 75.0769 35C28.1845 35 5.69223 65 0.307617 80Z"
                                    fill={svg || '#006838'}
                                />
                            </svg>
                        </Box>
                        <Chart
                            type={'radialBar'}
                            options={options}
                            height={options?.chart?.height || 80}
                            series={series}
                        />
                    </>
                }
            </Card>
        </>
    )
}

const CardUsingPerOptions = ({ title, content, ...props }) => {
    return (
        <React.Fragment>
            <Card
                {...props}
                elevation={0}
                sx={{ backgroundColor: alpha('#369FFF', 0.1) }}
            >
                <Box m={3}>
                    <Stack spacing={2}>
                        <Typography variant="h6">{title || '- -'}</Typography>
                        <Stack
                            direction="row"
                            spacing={2}
                            justifyContent={'space-between'}
                        >
                            <Box />
                            <Typography
                                variant="h3"
                                color={'primary'}
                                textAlign={'end'}
                                component="div"
                            >
                                {content ? content : '- -'}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            </Card>
        </React.Fragment>
    )
}

const Analytics = () => {
    const { data } = useGetStatsOfMeasure()
    const { config } = useDashboardConfig()
    const { start_date, end_date } = useAnalyzerStore()

    const contentRef = useRef(null)
    const reactToPrintFn = useReactToPrint({ contentRef })
    const { format } = useTranslateMeasurements()

    return (
        <Fragment>
            <div className="print-area" ref={contentRef}>
                <ContentBox className="analytics">
                    <Stack gap={2}>
                        <AnalyzerToolbar
                            disableExcel
                            onPrint={reactToPrintFn}
                        />
                        <Typography variant="h6">
                            รายงานสรุปข้อมูลสุขภาพ
                        </Typography>
                        <div className="display-none print-area-show">
                            <Typography variant="h6">
                                ระหว่างวันที่{' '}
                                {moment(start_date).format('DD/MM/YYYY')} ถึง{' '}
                                {moment(end_date).format('DD/MM/YYYY')}
                            </Typography>
                        </div>

                        <Grid container spacing={3}>
                            <Grid item lg={3} md={6} sm={12} xs={12}>
                                <CardUsingPerOptions
                                    title={'เป้าหมายจำนวนผู้คัดกรอง'}
                                    content={config?.target || 'โปรดระบุ'}
                                />
                            </Grid>
                            <Grid item lg={3} md={6} sm={12} xs={12}>
                                <CardUsingPerOptions
                                    title={'ผู้รับการคัดกรองทั้งหมด'}
                                    content={data?.patient_count?.result}
                                />
                            </Grid>
                            <Grid item lg={3} md={6} sm={12} xs={12}>
                                <CardUsingPerOptions
                                    title={'จำนวนผลการคัดกรองทั้งหมด'}
                                    content={data?.measure_count?.result}
                                />
                            </Grid>
                            <Grid item lg={3} md={6} sm={12} xs={12}>
                                <CardUsingPerOptions
                                    title={'ประเภทการตรวจทั้งหมด'}
                                    content={data?.measure_type_count?.result}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={3}>
                            {data?.measure_count_and_percent?.result?.map(
                                (item, i) => (
                                    <Grid
                                        key={item.name + i}
                                        item
                                        lg={3}
                                        md={6}
                                        sm={12}
                                        xs={12}
                                    >
                                        <ChartX
                                            title={item.count}
                                            name={item.name}
                                            percent={item.percentage || 0}
                                        />
                                    </Grid>
                                )
                            )}
                        </Grid>
                        <ChartY
                            defaultSerial={
                                data?.count_of_measure_every_date?.series
                            }
                            categories={
                                data?.count_of_measure_every_date?.labels
                            }
                        />
                        <Stack spacing={2}>
                            {data?.criterial?.map((item, i) => (
                                <Card key={i + item.key} component={'section'}>
                                    <CardContent>
                                        <Typography variant="h6">
                                            {format(item.key)}
                                        </Typography>
                                        <Grid
                                            key={item.key}
                                            container
                                            columns={7}
                                            spacing={3}
                                        >
                                            {item.value.map((criteria, ii) => (
                                                <Grid
                                                    key={
                                                        item.key +
                                                        'criterial' +
                                                        ii
                                                    }
                                                    item
                                                    lg={1}
                                                    md={1}
                                                    sm={7}
                                                    xs={7}
                                                >
                                                    <CriteriaRadialChart
                                                        title={criteria.label}
                                                        percent={
                                                            (criteria.count *
                                                                100) /
                                                            item.total
                                                        }
                                                        level={criteria.level}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </Stack>
                </ContentBox>
                <DataTable isHideFilterHeader />
            </div>
        </Fragment>
    )
}

export default Analytics
